/**
 * symbolMap.ts
 *
 * Canonical bi-directional mapping between internal symbol ids
 * (e.g. "frxEURUSD", "BTCUSDT", "SPY") and provider-specific
 * symbol strings (TwelveData, Deriv, Binance).
 *
 * This file is the single source of truth for symbol translation.
 * Provider migrations swap out only the call site, not the mapping.
 */

import { FOREX_SYMBOLS, CRYPTO_SYMBOLS, INDICES_SYMBOLS } from '../constants/index.js';

// ---------------------------------------------------------------------------
// TwelveData symbol map
// ---------------------------------------------------------------------------

/**
 * Maps internal id → TwelveData symbol string.
 *
 * Forex: "frxEURUSD" → "EUR/USD"  (strip "frx", insert "/")
 * Crypto: "BTCUSDT"  → "BTC/USD"  (Binance USDT pairs → TD USD pairs)
 * Indices/ETFs: identical to internal id (SPY → SPY)
 *
 * Symbols with no TD support return `null`.
 */
const TWELVEDATA_SYMBOL_MAP: ReadonlyMap<string, string> = (() => {
  const entries: [string, string][] = [];

  // Forex — strip "frx" prefix, insert "/" after 3-char base
  // e.g. frxEURUSD → EUR/USD, frxXAUUSD → XAU/USD
  for (const s of FOREX_SYMBOLS) {
    const raw = s.id.replace(/^frx/, ''); // e.g. EURUSD, XAUUSD
    // All current forex symbols are 6 chars (3+3)
    if (raw.length >= 6) {
      const base = raw.slice(0, 3);
      const quote = raw.slice(3);
      entries.push([s.id, `${base}/${quote}`]);
    }
  }

  // Crypto — Binance uses USDT; TwelveData uses USD
  // BTCUSDT → BTC/USD, ETHUSDT → ETH/USD, etc.
  for (const s of CRYPTO_SYMBOLS) {
    const id = s.id; // e.g. BTCUSDT
    if (id.endsWith('USDT')) {
      const base = id.slice(0, -4); // strip "USDT"
      entries.push([id, `${base}/USD`]);
    } else if (id.endsWith('USD')) {
      const base = id.slice(0, -3);
      entries.push([id, `${base}/USD`]);
    }
    // Others unsupported → omit (will return null)
  }

  // Indices/ETFs — same id used by TwelveData
  for (const s of INDICES_SYMBOLS) {
    entries.push([s.id, s.id]);
  }

  return new Map(entries);
})();

/**
 * Reverse map: TwelveData symbol → internal id.
 * Built from TWELVEDATA_SYMBOL_MAP.
 */
const TWELVEDATA_REVERSE_MAP: ReadonlyMap<string, string> = (() => {
  const entries: [string, string][] = [];
  for (const [internal, td] of TWELVEDATA_SYMBOL_MAP) {
    // Only add the first mapping in case of collision (shouldn't happen)
    if (!entries.some(([k]) => k === td)) {
      entries.push([td, internal]);
    }
  }
  return new Map(entries);
})();

// ---------------------------------------------------------------------------
// Deriv symbol map
// ---------------------------------------------------------------------------

/**
 * Maps internal id → Deriv symbol string.
 * For forex, Deriv uses the same "frxEURUSD" style as our internal ids.
 * Crypto and indices are not supported by Deriv → null.
 */
const DERIV_SYMBOL_MAP: ReadonlyMap<string, string> = (() => {
  const entries: [string, string][] = [];
  for (const s of FOREX_SYMBOLS) {
    // Deriv forex symbols mirror our internal ids exactly
    entries.push([s.id, s.id]);
  }
  return new Map(entries);
})();

// ---------------------------------------------------------------------------
// Binance symbol map
// ---------------------------------------------------------------------------

/**
 * Maps internal id → Binance symbol string.
 * For crypto, Binance uses uppercase concatenated pairs (BTCUSDT).
 * Forex and indices are not supported → null.
 */
const BINANCE_SYMBOL_MAP: ReadonlyMap<string, string> = (() => {
  const entries: [string, string][] = [];
  for (const s of CRYPTO_SYMBOLS) {
    // Binance symbols are identical to our internal crypto ids
    entries.push([s.id, s.id]);
  }
  return new Map(entries);
})();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert an internal symbol id to TwelveData format.
 *
 * @param internalId - e.g. "frxEURUSD", "BTCUSDT", "SPY"
 * @returns TwelveData symbol string (e.g. "EUR/USD") or `null` if unsupported.
 */
export function toTwelveDataSymbol(internalId: string): string | null {
  return TWELVEDATA_SYMBOL_MAP.get(internalId) ?? null;
}

/**
 * Convert a TwelveData symbol back to our internal id.
 *
 * @param tdSymbol - e.g. "EUR/USD", "BTC/USD", "SPY"
 * @returns Internal id or `null` if unknown.
 */
export function fromTwelveDataSymbol(tdSymbol: string): string | null {
  return TWELVEDATA_REVERSE_MAP.get(tdSymbol) ?? null;
}

/**
 * Convert an internal symbol id to Deriv format.
 *
 * @param internalId - e.g. "frxEURUSD"
 * @returns Deriv symbol string or `null` if not supported by Deriv.
 */
export function toDerivSymbol(internalId: string): string | null {
  return DERIV_SYMBOL_MAP.get(internalId) ?? null;
}

/**
 * Convert an internal symbol id to Binance format.
 *
 * @param internalId - e.g. "BTCUSDT"
 * @returns Binance symbol string or `null` if not supported by Binance.
 */
export function toBinanceSymbol(internalId: string): string | null {
  return BINANCE_SYMBOL_MAP.get(internalId) ?? null;
}
